/**
 * Unsaved result entries per tab (sessionStorage), so a session loss, a
 * reload or a failed save does not lose them
 * (documentation/result-entry-drafts.md).
 *
 * Key: `gpfits:draft:${userId}:${page}:${scopeId}`. Values are ids and
 * entered values only: no tokens, no names.
 */
export const DRAFT_PREFIX = "gpfits:draft:";

export type DraftPage = "bulk-entry" | "measurement" | "timer";

export function draftKey(
  userId: string,
  page: DraftPage,
  scopeId: string,
): string {
  return `${DRAFT_PREFIX}${userId}:${page}:${scopeId}`;
}

function safeStorage(): Storage | null {
  try {
    return typeof sessionStorage !== "undefined" ? sessionStorage : null;
  } catch {
    return null;
  }
}

export function readDraft<T>(key: string): T | null {
  const raw = safeStorage()?.getItem(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function writeDraft(key: string, value: unknown): void {
  try {
    safeStorage()?.setItem(key, JSON.stringify(value));
  } catch {
    // quota or private mode: the form keeps working without a draft
  }
}

export function removeDraft(key: string): void {
  safeStorage()?.removeItem(key);
}

function draftKeys(): string[] {
  const storage = safeStorage();
  if (!storage) return [];
  const keys: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (key?.startsWith(DRAFT_PREFIX)) keys.push(key);
  }
  return keys;
}

/** On logout: drafts of every user. */
export function clearAllDrafts(): void {
  draftKeys().forEach((key) => safeStorage()?.removeItem(key));
}

/** On login: drafts that belong to another user. */
export function clearDraftsOfOtherUsers(userId: string): void {
  const own = `${DRAFT_PREFIX}${userId}:`;
  draftKeys()
    .filter((key) => !key.startsWith(own))
    .forEach((key) => safeStorage()?.removeItem(key));
}
